import React, { useState, useRef, useMemo, useContext, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { useTranslation } from "react-i18next";
import { useLoaderData } from "react-router-dom";
import { ISettingUser } from "../../types/settings";
import Button from "../../components/ui/button/Button";
import SidebarModifyUser from "../../components/sidebarModifyUser/SidebarModifyUser";
import { settingsService } from "../../api/settingsService/settingsService";
import { toast } from "react-toastify";
import SidebarAddUser from "../../components/sidebarAddUser/SidebarAddUser";
import { AuthContext } from "../../context/authContext";
import { IAuthContext } from "../../types/auth";

const Settings: React.FC = () => {
    const { t } = useTranslation();

    const { user } = useContext(AuthContext) as IAuthContext;

    const { settingsApi } = useLoaderData() as { settingsApi: ISettingUser[] };

    const [contacts, setContacts] = useState<ISettingUser[] | null>(null);

    const canGo = useRef(true);
    useEffect(() => {
        if (canGo.current) {
            canGo.current = false;
            setContacts(settingsApi);
        }
    }, []);

    const loggedUserIsAccountAdmin = useMemo(() => {
        if (contacts && contacts.length > 0 && !!user) {
            return !!contacts.find(contact => contact.isAccountAdministrator && contact.id === user.contactId);
        }

        return false;
    }, [user, contacts]);

    // Delete
    const [isOpenModalDeleteUser, setIsOpenModalDeleteUser] = useState(false);
    const [userToDelete, setUserToDelete] = useState<ISettingUser | null>(null);

    // Modify
    const [isOpenSidebarModifyUser, setIsOpenSidebarModifyUser] = useState(false);
    const [userToModify, setUserToModify] = useState<ISettingUser | null>(null);

    const [visualizationType, setVisualizationType] = useState<"grid" | "table">('grid');

    const gridRef = useRef<any>(null);
    const columnDefs = useMemo(() => [
        {
            field: 'name', headerName: t('screens.settings.tableHeaders.user'), cellRenderer: (data: any) => {
                return (
                    <div>{data.data.name} {data.data.surname}</div>
                )
            }
        },
        { field: 'email', headerName: t('screens.settings.tableHeaders.email') },
        { field: 'phone', headerName: t('screens.settings.tableHeaders.phone') },
        { field: 'role.value', headerName: t('screens.settings.tableHeaders.role') },
        {
            field: 'status', headerName: t('screens.settings.tableHeaders.properties'), cellRenderer: (data) => {
                return (
                    <div
                        onClick={() => handleChangeAdmin(data.data)}
                        className="cursor-pointer text-[13px] leading-[16px] text-black flex items-center h-full"
                    >
                        <div className="mr-[5px] shrink-0 relative h-3 w-3 border-[1.6px] border-[#757575] rounded-full flex items-center justify-center">
                            {
                                data.data.isAccountAdministrator &&
                                <div className="absolute left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] h-[6px] w-[6px] bg-red rounded-full"></div>
                            }
                        </div>
                        <div>{t('screens.settings.accountAdministrator')}</div>
                    </div>
                )
            }
        },
        {
            field: 'status', headerName: '', cellRenderer: (data) => {
                return (
                    <div className="flex h-full items-center">
                        {
                            loggedUserIsAccountAdmin &&
                            <>
                                {
                                    data.data.id !== user?.contactId &&
                                    <div onClick={openDeleteUser(data.data)} className="h-full mr-2 flex items-center cursor-pointer">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path className="fill-red" d="M14 10.9998H22V12.9998H14V10.9998ZM4.5 8.55176C4.5 10.5468 6.005 12.0518 8 12.0518C9.995 12.0518 11.5 10.5468 11.5 8.55176C11.5 6.55676 9.995 5.05176 8 5.05176C6.005 5.05176 4.5 6.55676 4.5 8.55176ZM4 18.9998H14V17.9998C14 15.2428 11.757 12.9998 9 12.9998H7C4.243 12.9998 2 15.2428 2 17.9998V18.9998H4Z" />
                                        </svg>
                                    </div>
                                }

                                <div onClick={openModifyUser(data.data)}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full flex items-center cursor-pointer">
                                        <path d="M18.988 2.01196L21.988 5.01196L19.701 7.29996L16.701 4.29996L18.988 2.01196ZM8 16H11L18.287 8.71296L15.287 5.71296L8 13V16Z" className="fill-red" />
                                        <path d="M19 19H8.158C8.132 19 8.105 19.01 8.079 19.01C8.046 19.01 8.013 19.001 7.979 19H5V5H11.847L13.847 3H5C3.897 3 3 3.896 3 5V19C3 20.104 3.897 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V10.332L19 12.332V19Z" className="fill-red" />
                                    </svg>
                                </div>
                            </>
                        }
                    </div>
                )
            }
        },
    ], [contacts]);

    const onGridReady = () => {
        if (gridRef.current) {
            gridRef.current.api.setDomLayout('autoHeight');
        }
    }

    const openDeleteUser = (user: ISettingUser) => () => {
        if (loggedUserIsAccountAdmin) {
            setUserToDelete(user);
            setIsOpenModalDeleteUser(true);
        }
    }

    const openModifyUser = (user: ISettingUser) => () => {
        if (loggedUserIsAccountAdmin) {
            setUserToModify(user);
            setIsOpenSidebarModifyUser(true);
        }
    }

    const [loadingDeleteContact, setLoadingDeleteContact] = useState(false);
    const handleDeleteUser = async () => {
        if (!!userToDelete && !!userToDelete.id && loggedUserIsAccountAdmin === true && userToDelete.id !== user?.contactId) {
            setLoadingDeleteContact(true);
            const deleteUserResponse = await settingsService.deleteContact({
                id: userToDelete.id
            });

            if (deleteUserResponse.success) {
                const getUsersResponse = await settingsService.getContacts();

                if (getUsersResponse.success) {
                    setContacts(getUsersResponse.responseBody.contacts);
                    setIsOpenModalDeleteUser(false);
                } else {
                    window.location.reload();
                }
            } else {
                console.log("error deleting contact");
                toast.error(t('errors.errorDeletingContact'));
            }
            setLoadingDeleteContact(false);
        }
    }

    const [isOpenSidebarAddUser, setIsOpenSidebarAddUser] = useState(false);

    const [userToBecomeAdmin, setUserToBecomeAdmin] = useState<ISettingUser | null>(null);
    const [isOpenModatChangeAdmin, setIsOpenModatChangeAdmin] = useState<boolean>(false);
    const handleChangeAdmin = (settingUser: ISettingUser) => {
        if (loggedUserIsAccountAdmin && settingUser.id !== user?.contactId) {
            setUserToBecomeAdmin(settingUser);
            setIsOpenModatChangeAdmin(true);
        }
    }

    const [loadingChangeAdmin, setLoadingChangeAdmin] = useState<boolean>(false);
    const handleRequestChangeAdmin = async () => {
        if (!!userToBecomeAdmin && !!userToBecomeAdmin.id && loggedUserIsAccountAdmin === true) {
            setLoadingChangeAdmin(true);
            const changeAdminResponse = await settingsService.changeAdmin({
                id: userToBecomeAdmin.id
            });

            if (changeAdminResponse.success) {
                const getUsersResponse = await settingsService.getContacts();

                if (getUsersResponse.success) {
                    setContacts(getUsersResponse.responseBody.contacts);
                    setIsOpenModatChangeAdmin(false);
                } else {
                    window.location.reload();
                }
            } else {
                console.log("error deleting contact");
                toast.error(t('errors.errorDeletingContact'));
            }
            setLoadingChangeAdmin(false);
        }
    }

    return (
        <div className="p-6 md:p-8">

            {/* Modal delete user */}
            {
                isOpenModalDeleteUser === true && !!userToDelete &&
                <div className="fixed top-0 left-0 z-10 h-[100dvh] w-screen bg-[#00000080] flex items-center justify-center">
                    <div className="bg-white h-[65%] w-[95%] lg:w-[50%] max-w-[700px] flex flex-col">
                        <div className="shrink-0 flex justify-between border-b p-[30px] items-center">
                            <div className="font-bold text-lg">
                                {t('screens.settings.modalDeleteUser.deleteUser')}
                            </div>

                            <div onClick={() => setIsOpenModalDeleteUser(false)} className="cursor-pointer">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                        </div>

                        {/* MAIN CONTENT */}
                        <div className="grow py-8 px-[30px] select-none overflow-auto relative">

                            <p className="mb-5" dangerouslySetInnerHTML={{
                                __html: t('screens.settings.modalDeleteUser.text', {
                                    name: userToDelete?.name,
                                    surname: userToDelete?.surname
                                }).toString()
                            }}>
                            </p>

                            <p>
                                {t('screens.settings.modalDeleteUser.confirmText')}
                            </p>
                        </div>

                        <div className="select-none px-[30px] py-6 border-t flex items-center justify-end">
                            <Button
                                callback={handleDeleteUser}
                                loading={loadingDeleteContact}
                            >
                                {t('screens.settings.modalDeleteUser.deleteUser')}
                            </Button>
                        </div>

                    </div>
                </div>
            }

            {/* Modal change admin */}
            {
                isOpenModatChangeAdmin === true && !!userToBecomeAdmin &&
                <div className="fixed top-0 left-0 z-10 h-[100dvh] w-screen bg-[#00000080] flex items-center justify-center">
                    <div className="bg-white h-[65%] w-[95%] lg:w-[50%] max-w-[700px] flex flex-col">
                        <div className="shrink-0 flex justify-between border-b p-[30px] items-center">
                            <div className="font-bold text-lg">
                                {t('screens.settings.modalChangeAdmin.changeAdmin')}
                            </div>

                            <div onClick={() => setIsOpenModatChangeAdmin(false)} className="cursor-pointer">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                        </div>

                        {/* MAIN CONTENT */}
                        <div className="grow py-8 px-[30px] select-none overflow-auto relative">

                            <p className="mb-5" dangerouslySetInnerHTML={{
                                __html: t('screens.settings.modalChangeAdmin.text', {
                                    name: userToBecomeAdmin?.name,
                                    surname: userToBecomeAdmin?.surname
                                }).toString()
                            }}>
                            </p>

                            <p>
                                {t('screens.settings.modalChangeAdmin.confirmText')}
                            </p>
                        </div>

                        <div className="select-none px-[30px] py-6 border-t flex items-center justify-end">
                            <Button
                                callback={handleRequestChangeAdmin}
                                loading={loadingChangeAdmin}
                            >
                                {t('screens.settings.modalChangeAdmin.button')}
                            </Button>
                        </div>

                    </div>
                </div>
            }

            {/* Modify User */}
            <SidebarModifyUser
                loggedUserIsAccountAdmin={loggedUserIsAccountAdmin}
                user={userToModify}
                callbackCloseSidebar={() => setIsOpenSidebarModifyUser(false)}
                isOpen={isOpenSidebarModifyUser}
                callbackUpdateContacts={(contacts) => setContacts(contacts)}
            />

            <SidebarAddUser
                loggedUserIsAccountAdmin={loggedUserIsAccountAdmin}
                isOpen={isOpenSidebarAddUser}
                callbackCloseSidebar={() => setIsOpenSidebarAddUser(false)}
                callbackUpdateContacts={(contacts) => setContacts(contacts)}
            />

            <div className="md:flex items-start justify-between mb-6 md:mb-[14px]">
                <h1 className="mb-4 md:mb-0 text-[32px] font-bold leading-[37.5px]">
                    {t('screens.settings.heading')}
                </h1>

                {
                    loggedUserIsAccountAdmin &&
                    <Button
                        callback={() => (setIsOpenSidebarAddUser(true))}
                    >
                        <div className="flex items-center">
                            <span className="mr-[12px]">{t('common.sidebars.addUser.heading')}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-[18px] h-[18px]">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                            </svg>
                        </div>
                    </Button>
                }
            </div>

            <div className="mb-[26px]">
                {/* Grid or Table buttons */}
                <div className="hidden md:flex items-center">
                    <div
                        className={"mr-[10px] cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" + (visualizationType === 'grid' ? ' bg-red' : '')}
                        onClick={() => setVisualizationType('grid')}
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 0H6V6H0V0ZM8 0H14V6H8V0ZM0 8H6V14H0V8Z" fill={visualizationType === 'grid' ? "#FFFFFF" : "#6F6F6F"} />
                            <path d="M14 8H8V14H14V8Z" fill={visualizationType === 'grid' ? "#FFFFFF" : "#6F6F6F"} />
                        </svg>
                    </div>

                    <div
                        className={"cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" + (visualizationType === 'table' ? ' bg-red' : '')}
                        onClick={() => setVisualizationType('table')}
                    >
                        <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 0H16V2H0V0ZM0 4H16V6H0V4ZM0 8H16V10H0V8Z" fill={visualizationType === 'table' ? "#FFFFFF" : "#6F6F6F"} />
                        </svg>
                    </div>
                </div>
            </div>

            {
                visualizationType === 'grid' &&
                <div className="grid grid-cols-12 gap-6">
                    {
                        contacts !== null && contacts.length > 0 &&

                        contacts.map((settingUser, index) => {
                            return (
                                <div
                                    className={"col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 md:flex md:flex-col md:justify-between rounded-[8px] shadow-card select-none p-4 bg-lightGrey"}
                                    key={index}
                                >
                                    <div className="border-b overflow-hidden">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="text-2xl leading-[29px]">
                                                <div>{settingUser.name}</div>
                                                <div className="font-bold">{settingUser.surname}</div>
                                            </div>

                                            <div className="h-[52px] w-[52px] rounded-full bg-black flex items-center justify-center">
                                                <div className="uppercase text-xl leading-[24px] text-white">{settingUser.name[0]}{settingUser.surname[0]}</div>
                                            </div>
                                        </div>

                                        <div className="mb-3 overflow-hidden">
                                            <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.settings.email')}</div>
                                            <div className="text-lg text-ellipsis overflow-hidden leading-[22px] text-black">{settingUser.email}</div>
                                        </div>

                                        <div className="mb-3">
                                            <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.settings.phone')}</div>
                                            <div className="text-lg text-ellipsis overflow-hidden leading-[22px] text-black">{settingUser.phone}</div>
                                        </div>

                                        <div className="mb-3">
                                            <div className="uppercase text-red font-semibold text-sm leading-[17px]">{t('screens.settings.role')}</div>
                                            <div className="text-lg text-ellipsis overflow-hidden leading-[22px] text-black">{settingUser.role.value}</div>
                                        </div>
                                    </div>

                                    <div className="pt-3">
                                        <div className="mb-3">
                                            <div className="uppercase text-red font-semibold text-sm leading-[17px] mb-3">{t('screens.settings.properties')}</div>
                                            <div
                                                onClick={() => handleChangeAdmin(settingUser)}
                                                className="cursor-pointer text-[13px] leading-[16px] text-black flex items-center"
                                            >
                                                <div className="mr-[5px] relative h-3 w-3 border-[1.6px] border-[#757575] rounded-full flex items-center justify-center">
                                                    {
                                                        settingUser.isAccountAdministrator &&
                                                        <div className="absolute left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] h-[6px] w-[6px] bg-red rounded-full"></div>
                                                    }
                                                </div>
                                                <div>
                                                    {t('screens.settings.accountAdministrator')}
                                                </div>
                                            </div>
                                        </div>

                                        {
                                            loggedUserIsAccountAdmin &&
                                            <div className="flex items-center">
                                                {
                                                    settingUser.id !== user?.contactId &&
                                                    <div onClick={openDeleteUser(settingUser)} className="bg-red rounded-[8px] p-[6px] mr-2 cursor-pointer">
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M14 10.9998H22V12.9998H14V10.9998ZM4.5 8.55176C4.5 10.5468 6.005 12.0518 8 12.0518C9.995 12.0518 11.5 10.5468 11.5 8.55176C11.5 6.55676 9.995 5.05176 8 5.05176C6.005 5.05176 4.5 6.55676 4.5 8.55176ZM4 18.9998H14V17.9998C14 15.2428 11.757 12.9998 9 12.9998H7C4.243 12.9998 2 15.2428 2 17.9998V18.9998H4Z" fill="white" />
                                                        </svg>
                                                    </div>
                                                }

                                                <div onClick={openModifyUser(settingUser)} className="bg-red rounded-[8px] p-[6px] cursor-pointer">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M18.988 2.01196L21.988 5.01196L19.701 7.29996L16.701 4.29996L18.988 2.01196ZM8 16H11L18.287 8.71296L15.287 5.71296L8 13V16Z" fill="white" />
                                                        <path d="M19 19H8.158C8.132 19 8.105 19.01 8.079 19.01C8.046 19.01 8.013 19.001 7.979 19H5V5H11.847L13.847 3H5C3.897 3 3 3.896 3 5V19C3 20.104 3.897 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V10.332L19 12.332V19Z" fill="white" />
                                                    </svg>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </div>
                            )
                        })
                    }

                    {
                        contacts !== null && contacts.length === 0 &&
                        <div className="col-span-12">{t('screens.settings.emptyContacts')}</div>
                    }
                </div>
            }

            {
                visualizationType === 'table' &&
                <div className="ag-theme-alpine" style={{ width: '100%', height: '100%' }}>
                    <AgGridReact
                        ref={gridRef}
                        rowData={contacts} // Row Data for Rows 
                        columnDefs={columnDefs} // Column Defs for Columns 
                        defaultColDef={{
                            flex: 1,
                            minWidth: 100,
                        }}
                        onGridReady={onGridReady}
                        suppressRowClickSelection
                        suppressCellFocus
                        suppressMovableColumns
                    />
                </div>
            }
        </div>
    )
}

export default Settings;